import { clearProductDetail as clearProductDetailInfo } from '../../modules/productsListing/actionCreators'
import { getMarketplaceReferralTemplateSalesforce } from '../../modules/marketplaceMappingManager/action'
import { setCloudMarketplaceProductListing } from '../../modules/productListingFilter/action'
import type { CloudMarketplace } from '../../modules/productsListing/types'
import { useGetProductDetail } from '../../common/utils/hooks/useGetProductDetail'
import {
  ChooseTemplateModalMarketplace,
  type ChooseTemplateModalTemplate,
} from '../PrivateOfferFormV2/ChooseTemplateModal/ChooseTemplateModal'
import React, { lazy, useCallback, useEffect, useMemo, useState } from 'react'
import { isEmpty } from 'lodash'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { CRMTypes, useAccountId } from '@labrav/common'
import { RootState, useDispatch, useSelector } from '../../store/hooks'
import {
  clearPrivateOfferInCreation,
  getCurrentPrivateOfferInCreation,
  getDefaultPrivateOfferInCreation,
  sendPrivateOffer,
} from '../../modules/privateOffer/privateOfferCreation/action'
import { useStyles } from './EditPrivateOfferWrapperV2.styles'
import { OffersWithErrors } from '../../modules/privateOffer/privateOfferCreation/reducer'
import { autoSave } from '@labrav/common'
import { SelectChangeEvent } from '@mui/material/Select'
import { useFlagValue } from '@labrav/flags'
import { actionTypeWrapper, useValidateCloudRoute } from '@labrav/common'
import { useSyncFlyoutRouteParams } from '../../common/utils/hooks/useSyncFlyoutRouteParams'
import {
  LoadingTypes,
  isLoading as Loading,
  useCurrentCloudProvider,
} from '@labrav/common'
import { LoadingLogo } from '@labrav/react-components'

const PrivateOfferContainerAddEditFormik = lazy(() =>
  import(
    './PrivateOfferContainerAddEditFormik/PrivateOfferContainerAddEditFormik'
  ).then(m => ({
    default: m.PrivateOfferContainerAddEditFormik,
  }))
)

export const EditPrivateOfferWrapperV2: React.FC<{
  externalBaseURL?: string
  search?: string
  crmOpportunityId?: string
  crmObjectLink?: string
  isSidePanelVisible?: boolean
  isSalesforceContext?: boolean
}> = ({
  externalBaseURL,
  search,
  crmOpportunityId,
  crmObjectLink,
  isSidePanelVisible = false,
  isSalesforceContext = false,
}) => {
  const {
    cloud: routeCloud,
    accountId,
    productId = '',
    privateOfferId,
    secureKey: secureKeyFromURL,
    partnerId: partnerIdFromURL,
  } = useParams<{
    cloud: string
    accountId: string
    productId: string
    privateOfferId: string
    secureKey: string
    partnerId: string
  }>()
  const isHubspotRoute = useLocation().pathname.includes('/hubspot')
  const isSalesforceRoute = useLocation().pathname.includes('/salesforce')

  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''

  const routeAccountId =
    accountId ||
    (isSalesforceRoute ? partnerIdFromURL : '') ||
    fallbackAccountId

  const hasValidAccountId = accountId
    ? !isEmpty(accountId)
    : !isEmpty(fallbackAccountId)
  const hasExternalBaseURL = !!externalBaseURL

  const isDefaultOfferValuesLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.GET_DEFAULT_OFFERS)
  )
  const isOfferLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.GENERAL)
  )

  useSyncFlyoutRouteParams(!!hasExternalBaseURL)
  // Validate cloud type and redirect if unsupported
  useValidateCloudRoute('flyout', '/flyout', hasExternalBaseURL)
  const { classes } = useStyles(hasExternalBaseURL)
  const urlSearchParams = new URLSearchParams(search)
  const dispatch = useDispatch()
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const product = useGetProductDetail({
    accountId: routeAccountId,
    productId,
    cloudMarketplace: selectedCloudTypeProductListing,
  })

  useEffect(() => {
    return () => {
      dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          clearProductDetailInfo()
        )
      )
    }
  }, [dispatch, selectedCloudTypeProductListing])

  const footerWidth =
    externalBaseURL && !isSidePanelVisible ? '100%' : 'calc(100% - 64px)'
  const tabTop = externalBaseURL ? '0px' : '64px'

  const effectiveProductId = product?.productId || productId

  // Set cloud provider from product when in external context (no cloud in URL)
  useEffect(() => {
    if (
      hasExternalBaseURL &&
      product?.cloudMarketplace &&
      product?.cloudMarketplace !== selectedCloudTypeProductListing
    ) {
      dispatch(setCloudMarketplaceProductListing(product.cloudMarketplace))
    }
  }, [
    hasExternalBaseURL,
    product?.cloudMarketplace,
    selectedCloudTypeProductListing,
    dispatch,
  ])

  const navigate = useNavigate()
  const offer = useSelector<OffersWithErrors | undefined>(
    state =>
      state.privateOffersInCreation.currentPrivateOfferInCreation[
        effectiveProductId
      ]
  )
  const { value: isMappingManagerUiEnabled } = useFlagValue('mappingManager')
  // Create Template Modal utils
  const marketplaceTemplates = useSelector(
    (state: RootState) =>
      (state.marketplaceMappingManager?.templateDetails ||
        []) as ChooseTemplateModalTemplate[]
  )
  const coSellTemplates = useSelector(
    (
      state: RootState & {
        coSellMappingManager?: {
          templateDetails?: ChooseTemplateModalTemplate[]
        }
      }
    ) =>
      (state.coSellMappingManager?.templateDetails ||
        []) as ChooseTemplateModalTemplate[]
  )
  const templatesData = isHubspotRoute ? coSellTemplates : marketplaceTemplates

  const crmObjectId = new URLSearchParams(search)
    .get('hs_object_id')
    ?.toLowerCase()

  const crmId = useSelector<string | undefined>(
    state => state.AccountData.user.partnerData?.crmId || ''
  )
  const isDefaultOfferDataLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.FETCH_REFERRAL_FORM_DATA)
  )
  const isSfTemplateLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.FETCH_CURRENT_REFERRAL_TEMPLATE)
  )
  const hasMultipleActiveTemplates = templatesData.length > 1
  const hasSingleActiveTemplate = templatesData.length == 1
  const singleTemplateId = templatesData[0]?.templateId
  const [isChooseTemplateModalResponded, setIsChooseTemplateModalResponded] =
    useState(false)
  const [templateModal, setTemplateModal] = useState<boolean>(true)
  const [shouldFetchOffer, setShouldFetchOffer] = useState(false)

  const searchParams = new URLSearchParams(search)
  const templateIdFromParams = searchParams.get('templateId')
  const cameFromCreateModal = searchParams.get('from') === 'createModal'

  const [selectedTemplate, setSelectedTemplate] = useState<string | undefined>(
    templateIdFromParams ||
      (hasSingleActiveTemplate ? singleTemplateId : undefined)
  )

  const closeTemplateModal = () => {
    setTemplateModal(false)
  }
  const handleSkip = () => {
    closeTemplateModal()
    setIsChooseTemplateModalResponded(true)
  }

  const handleContinueClick = async () => {
    if (selectedTemplate) {
      await setShouldFetchOffer(true)
      const newSearchParams = new URLSearchParams(search)
      newSearchParams.set('templateId', selectedTemplate)
      await closeTemplateModal()
      await navigate(
        {
          search: `?${newSearchParams.toString()}`,
        },
        { replace: true }
      )
    }
    await setIsChooseTemplateModalResponded(true)
  }

  const handleTemplateSelectChange = (event: SelectChangeEvent) => {
    setSelectedTemplate(event.target.value as string)
  }

  useEffect(() => {
    if (hasSingleActiveTemplate && singleTemplateId) {
      setSelectedTemplate(singleTemplateId)
      setShouldFetchOffer(true)
    }
  }, [hasSingleActiveTemplate, singleTemplateId])

  // When create modal (or another entry) already put templateId in the URL,
  // apply it and fetch default offer data without showing the choose-template modal.
  useEffect(() => {
    if (!templateIdFromParams) return
    setSelectedTemplate(templateIdFromParams)
    setShouldFetchOffer(true)
    setIsChooseTemplateModalResponded(true)
  }, [templateIdFromParams])

  const privateOffer = useMemo(() => {
    return offer || ({} as OffersWithErrors)
  }, [offer])

  useEffect(() => {
    const crmObjectIdToUse = crmObjectId || crmOpportunityId
    const shouldFetchOfferCondition = isSalesforceRoute
      ? isEmpty(privateOfferId)
      : shouldFetchOffer
    if (
      shouldFetchOfferCondition &&
      crmId &&
      crmObjectIdToUse &&
      selectedTemplate &&
      !!product
    ) {
      const init = async () => {
        const isAzureHubspotRoute =
          isHubspotRoute && routeCloud?.toLowerCase() === 'azure'
        if (isSalesforceRoute || isAzureHubspotRoute) {
          await dispatch(
            getMarketplaceReferralTemplateSalesforce(
              crmId,
              routeAccountId,
              selectedTemplate,
              false,
              isHubspotRoute ? CRMTypes.hubspot : undefined
            )
          )
        }
        await dispatch(
          getDefaultPrivateOfferInCreation({
            productId,
            crmOpportunityId,
            labraNewUiRevamp: true,
            crmId,
            crmObjectId: crmObjectIdToUse || '',
            templateId: selectedTemplate,
            accountId: routeAccountId,
            isHubspot: isHubspotRoute,
            cloudMarketplace: routeCloud?.toUpperCase() as CloudMarketplace,
          })
        )
      }
      init()
      setShouldFetchOffer(false)
    }
  }, [
    shouldFetchOffer,
    crmId,
    crmObjectId,
    effectiveProductId,
    productId,
    crmOpportunityId,
    routeAccountId,
    isHubspotRoute,
    isSalesforceRoute,
    routeCloud,
    product,
    privateOfferId,
  ])

  useEffect(() => {
    if (!isEmpty(privateOfferId)) return
    return () => {
      dispatch(clearPrivateOfferInCreation(effectiveProductId))
    }
  }, [dispatch, effectiveProductId, privateOfferId])

  useEffect(() => {
    if (
      isHubspotRoute &&
      routeCloud?.toLowerCase() === 'azure' &&
      isEmpty(privateOfferId) &&
      selectedTemplate &&
      crmId &&
      (crmObjectId || crmOpportunityId)
    ) {
      return
    }
    if (
      hasValidAccountId &&
      !isEmpty(productId) &&
      (accountId ? routeAccountId === accountId : true)
    ) {
      if (!!product && effectiveProductId) {
        dispatch(
          getCurrentPrivateOfferInCreation({
            productId: effectiveProductId,
            partnerId: routeAccountId,
            privateOfferId: privateOfferId || '',
            crmOpportunityId,
            labraNewUiRevamp: true,
          })
        )
      }
    }
  }, [
    routeAccountId,
    accountId,
    hasValidAccountId,
    product,
    effectiveProductId,
    productId,
    privateOfferId,
    crmOpportunityId,
    selectedCloudTypeProductListing,
    selectedTemplate,
    crmId,
    crmObjectId,
    isHubspotRoute,
    routeCloud,
  ])
  const baseURL = useMemo(() => {
    if (isSalesforceRoute)
      return `/external-apps/salesforce/partners/${routeAccountId}/flyout/products-listing`
    if (externalBaseURL) return externalBaseURL
    if (routeCloud && routeAccountId) {
      return `/flyout/${routeCloud}/${routeAccountId}/products-listing`
    }
    return `/flyout/products-listing`
  }, [
    externalBaseURL,
    routeCloud,
    routeAccountId,
    isSalesforceRoute,
    routeAccountId,
  ])

  useEffect(() => {
    if (privateOffer.hasBeenSent && !isEmpty(privateOffer.privateOfferId)) {
      const privateOfferId = privateOffer.privateOfferId
      const privatOfferDetailsPath = externalBaseURL
        ? routeCloud && routeAccountId
          ? `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerIdFromURL}/cloud/${routeCloud}/accounts/${routeAccountId}/product-listing/${productId}/private-offers/${privateOfferId}/details`
          : `${baseURL}/${productId}/private-offers/${privateOfferId}/details`
        : `${baseURL}/${productId}/private-offers/${privateOfferId}/details`
      navigate(
        {
          pathname: privatOfferDetailsPath,
          search,
        },
        { replace: true }
      )
      return () => {
        dispatch(clearPrivateOfferInCreation(effectiveProductId))
      }
    } else if (
      !isEmpty(privateOffer.privateOfferId) &&
      privateOffer.noOfTimesSaved === 1
    ) {
      const privateOfferEditPath = externalBaseURL
        ? routeCloud && routeAccountId
          ? `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerIdFromURL}/cloud/${routeCloud}/accounts/${routeAccountId}/product-listing/${productId}/private-offers/${privateOffer.privateOfferId}/edit`
          : `${baseURL}/${productId}/private-offers/${privateOffer.privateOfferId}/edit`
        : `${baseURL}/${productId}/private-offers/${privateOffer.privateOfferId}/edit`

      navigate(
        {
          pathname: privateOfferEditPath,
          search,
        },
        { replace: true }
      )
    }
  }, [
    privateOffer.privateOfferId,
    privateOffer.hasBeenSent,
    privateOffer.noOfTimesSaved,
    baseURL,
    search,
    productId,
    externalBaseURL,
  ])

  const savePrivateOffer = useCallback(
    async (saveAsDraft: boolean, formikValues: OffersWithErrors) => {
      const creatorEmail = urlSearchParams?.get('creator_email')
      const creatorName = urlSearchParams?.get('creator_name')
      const hasCreatorData = hasExternalBaseURL && creatorEmail && creatorName

      dispatch(
        autoSave(
          'containerPrivateOffer',
          async () =>
            await dispatch(
              sendPrivateOffer({
                partnerId: routeAccountId || '',
                productId,
                saveAsDraft,
                crmOpportunityId,
                crmObjectLink,
                formikOffer: formikValues,
                isV2: true,
                autoSave: false,
                ...(hasCreatorData
                  ? {
                      creatorData: {
                        creatorEmail,
                        creatorName,
                      },
                    }
                  : {}),
              })
            )
        )
      )
    },
    [routeAccountId, productId, crmOpportunityId, crmObjectLink, search]
  )
  const showChooseTemplateModal =
    hasExternalBaseURL &&
    isMappingManagerUiEnabled &&
    hasMultipleActiveTemplates &&
    !privateOfferId &&
    !isChooseTemplateModalResponded &&
    !templateIdFromParams &&
    !cameFromCreateModal &&
    !isSalesforceRoute

  if (
    !isEmpty(privateOfferId) &&
    isOfferLoading &&
    isEmpty(privateOffer?.privateOfferId)
  ) {
    return <LoadingLogo />
  }

  if (
    !isEmpty(routeAccountId) &&
    !isEmpty(product) &&
    !isEmpty(product) &&
    !isDefaultOfferValuesLoading &&
    !isSfTemplateLoading
  ) {
    if (selectedCloudTypeProductListing === 'AWS') {
      return showChooseTemplateModal ? (
        <ChooseTemplateModalMarketplace
          templateModal={templateModal}
          selectedTemplate={selectedTemplate || ''}
          handleContinueClick={handleContinueClick}
          handleSkip={handleSkip}
          handleTemplateSelectChange={handleTemplateSelectChange}
          templates={templatesData}
        />
      ) : (
        !isDefaultOfferDataLoading && (
          <PrivateOfferContainerAddEditFormik
            product={product}
            footerWidth={footerWidth}
            conditionalClassNamesForHubspot={classes}
            offset={hasExternalBaseURL ? 150 : 260}
            savePrivateOffer={savePrivateOffer}
            hideStakeholders={hasExternalBaseURL}
            tabTop={tabTop}
            cloud={selectedCloudTypeProductListing}
            isSalesforceContext={isSalesforceContext}
          />
        )
      )
    } else if (selectedCloudTypeProductListing === 'AZURE') {
      return (
        <PrivateOfferContainerAddEditFormik
          product={product}
          footerWidth={footerWidth}
          conditionalClassNamesForHubspot={classes}
          offset={hasExternalBaseURL ? 150 : 260}
          savePrivateOffer={savePrivateOffer}
          hideStakeholders={hasExternalBaseURL}
          tabTop={tabTop}
          cloud={selectedCloudTypeProductListing}
          isSalesforceContext={isSalesforceContext}
        />
      )
    }
  } else {
    return <div data-testid="empty-div-private-offer-creation"></div>
  }
}
